import { useState, useEffect } from "react";
import { getEditablePopupConfig } from "@/components/feature/PremiumLockedButton";

// ── Storage (legacy, kept for backward compat) ───────────────────────────────────────────────────────────────────

export const INSPECTOR_POPUP_KEY = "yg_inspector_premium_popup";

export interface InspectorAdvantage {
  id: string;
  icon: string;
  color: string;
  title: string;
  description: string;
}

export interface InspectorPopupData {
  title: string;
  subtitle: string;
  description: string;
  heroImageUrl: string;
  advantages: InspectorAdvantage[];
  stat1Value: string;
  stat1Label: string;
  stat2Value: string;
  stat2Label: string;
  stat3Value: string;
  stat3Label: string;
  ctaText: string;
  badgeText: string;
}

export const defaultInspectorPopupData: InspectorPopupData = {
  title: "Görsel İnspektör",
  subtitle: "Sitenizi canlı olarak fare ile düzenleyin",
  description: "Herhangi bir sayfanın üzerine hover ederek anında içerik değiştirebilir, bölümleri canlı editörle yönetebilir, proje ve ortak kartlarını sayfadan ayrılmadan güncelleyebilirsiniz.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=dark%20modern%20web%20development%20workspace%20with%20floating%20UI%20panels%20and%20glowing%20code%20editor%20overlay%20on%20a%20website%2C%20neon%20green%20and%20blue%20highlights%2C%20professional%20dashboard%20interface%2C%20dark%20background%20with%20subtle%20grid%20pattern%2C%20cinematic%20lighting%2C%20ultra%20high%20quality%20digital%20art&width=900&height=360&seq=insp-hero-001&orientation=landscape",
  advantages: [
    { id: "adv-1", icon: "ri-cursor-line",        color: "#10B981", title: "Hover ile Canlı Düzenleme",  description: "Her bölüme hover et, renkli çerçeve ve düzenleme araçları anında belirir" },
    { id: "adv-2", icon: "ri-pencil-fill",         color: "#60A5FA", title: "50+ Inline Metin Alanı",    description: "Başlıklar, açıklamalar, butonlar, istatistikler — tıkla ve düzenle" },
    { id: "adv-3", icon: "ri-building-2-line",     color: "#F59E0B", title: "Proje Kartı Editörü",       description: "12 alan: isim, konum, yıl, özellikler, YouTube ve harita linki" },
    { id: "adv-4", icon: "ri-arrow-go-back-line",  color: "#A78BFA", title: "Sınırsız Geri Alma",       description: "Ctrl+Z ile kaydetme noktasına dön, hiçbir değişiklikten korkma" },
    { id: "adv-5", icon: "ri-links-line",          color: "#F43F5E", title: "Ortak Yönetimi",            description: "11 alan: ortak adı, sektör, açıklama, etiket, proje sayısı" },
    { id: "adv-6", icon: "ri-layout-grid-line",    color: "#34D399", title: "4 Sayfa Desteği",           description: "Ana, Hakkımızda, Projeler ve Ortaklar sayfaları tek araçta" },
  ],
  stat1Value: "50+",
  stat1Label: "Düzenlenebilir Alan",
  stat2Value: "4",
  stat2Label: "Sayfa Desteği",
  stat3Value: "0",
  stat3Label: "Kod Gerektirmez",
  ctaText: "Aktifleştirmek için geliştirici ile iletişime geçin",
  badgeText: "Premium Özellik",
};

export function getInspectorPopupData(): InspectorPopupData {
  try {
    const raw = localStorage.getItem(INSPECTOR_POPUP_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<InspectorPopupData>;
      return { ...defaultInspectorPopupData, ...parsed };
    }
  } catch { /**/ }
  return defaultInspectorPopupData;
}

export function saveInspectorPopupData(data: InspectorPopupData): void {
  localStorage.setItem(INSPECTOR_POPUP_KEY, JSON.stringify(data));
}

// ── Popup Component ───────────────────────────────────────────────────────────

interface InspectorPremiumPopupProps {
  visible: boolean;
}

export default function InspectorPremiumPopup({ visible }: InspectorPremiumPopupProps) {
  const [imgError, setImgError] = useState(false);

  // Use unified editable config, fall back to legacy storage then default
  const getEffectiveData = (): InspectorPopupData => {
    const unified = getEditablePopupConfig("site_inspector");
    if (unified) {
      return {
        title: unified.title,
        subtitle: unified.subtitle,
        description: unified.description,
        heroImageUrl: unified.heroImageUrl,
        advantages: unified.advantages.map((a, i) => ({ id: `adv-${i}`, icon: a.icon, color: a.color, title: a.title, description: a.description })),
        stat1Value: unified.stats[0]?.value ?? "50+",
        stat1Label: unified.stats[0]?.label ?? "Düzenlenebilir Alan",
        stat2Value: unified.stats[1]?.value ?? "4",
        stat2Label: unified.stats[1]?.label ?? "Sayfa Desteği",
        stat3Value: unified.stats[2]?.value ?? "0",
        stat3Label: unified.stats[2]?.label ?? "Kod Gerektirmez",
        ctaText: unified.ctaText,
        badgeText: unified.badgeText,
      };
    }
    return getInspectorPopupData();
  };

  const [data, setData] = useState<InspectorPopupData>(getEffectiveData);

  useEffect(() => {
    if (visible) {
      setData(getEffectiveData());
      setImgError(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  return (
    <div
      className={`fixed z-[99999] transition-all duration-300 pointer-events-none ${
        visible ? "opacity-100 translate-x-0 pointer-events-auto" : "opacity-0 -translate-x-2"
      }`}
      style={{ width: "480px", left: "270px", top: "auto", bottom: "20px" }}
    >
      {/* Arrow */}
      <div
        className="absolute left-0 bottom-8 -translate-x-full border-8 border-transparent"
        style={{ borderRightColor: "#0F1729" }}
      ></div>

      <div
        className="bg-[#0A1020] border border-[#2B3654] rounded-2xl overflow-hidden shadow-2xl"
        style={{ boxShadow: "0 25px 60px rgba(0,0,0,0.7), 0 0 0 1px rgba(84,102,142,0.2)" }}
      >
        {/* ── Hero Image Area ── */}
        <div className="relative h-44 overflow-hidden flex-shrink-0">
          {!imgError ? (
            <img
              src={data.heroImageUrl}
              alt="Görsel İnspektör"
              className="w-full h-full object-cover object-top"
              onError={() => setImgError(true)}
            />
          ) : (
            <div
              className="w-full h-full"
              style={{
                background: "linear-gradient(135deg, #0A1020 0%, #141E35 40%, #0F1729 100%)",
                backgroundSize: "40px 40px",
                backgroundImage: "linear-gradient(rgba(84,102,142,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(84,102,142,0.1) 1px, transparent 1px)",
              }}
            />
          )}
          {/* Overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A1020] via-[#0A1020]/40 to-transparent"></div>
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A1020]/30 to-transparent"></div>

          {/* Badge */}
          <div className="absolute top-3 left-4">
            <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-500/20 border border-amber-500/40 backdrop-blur-sm">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-vip-crown-fill text-amber-400 text-xs"></i>
              </div>
              <span className="text-amber-300 text-xs font-semibold">{data.badgeText}</span>
            </div>
          </div>

          {/* Live badge */}
          <div className="absolute top-3 right-4 flex items-center gap-1.5 bg-black/50 backdrop-blur-sm border border-white/10 rounded-full px-2.5 py-1">
            <span className="w-1.5 h-1.5 rounded-full bg-red-400 animate-pulse"></span>
            <span className="text-white/70 text-[10px] font-medium">Kilitli</span>
          </div>

          {/* Title over hero */}
          <div className="absolute bottom-4 left-4 right-4">
            <h3 className="text-white font-black text-xl leading-tight">{data.title}</h3>
            <p className="text-[#A5B5CC] text-sm mt-0.5">{data.subtitle}</p>
          </div>
        </div>

        {/* ── Stats Bar ── */}
        <div className="grid grid-cols-3 border-b border-[#1E2B4A]">
          {[
            { val: data.stat1Value, lbl: data.stat1Label, color: "#10B981" },
            { val: data.stat2Value, lbl: data.stat2Label, color: "#60A5FA" },
            { val: data.stat3Value, lbl: data.stat3Label, color: "#F59E0B" },
          ].map((s, i) => (
            <div key={i} className={`py-3 text-center ${i < 2 ? "border-r border-[#1E2B4A]" : ""}`}>
              <p className="font-black text-lg" style={{ color: s.color }}>{s.val}</p>
              <p className="text-[#5E7398] text-[10px] mt-0.5 leading-tight">{s.lbl}</p>
            </div>
          ))}
        </div>

        {/* ── Description ── */}
        <div className="px-4 pt-4 pb-2">
          <p className="text-[#8BA0C0] text-xs leading-relaxed">{data.description}</p>
        </div>

        {/* ── Advantages Grid ── */}
        <div className="px-4 pb-4 grid grid-cols-2 gap-2 mt-1">
          {data.advantages.map((adv) => (
            <div
              key={adv.id}
              className="flex items-start gap-2.5 p-2.5 rounded-xl bg-[#141E35] border border-[#2B3654] hover:border-[#3a4d6e] transition-colors"
            >
              <div
                className="w-7 h-7 flex items-center justify-center rounded-lg flex-shrink-0"
                style={{ background: adv.color + "22" }}
              >
                <i className={`${adv.icon} text-sm`} style={{ color: adv.color }}></i>
              </div>
              <div className="min-w-0">
                <p className="text-white text-[11px] font-semibold leading-tight">{adv.title}</p>
                <p className="text-[#5E7398] text-[10px] leading-relaxed mt-0.5">{adv.description}</p>
              </div>
            </div>
          ))}
        </div>

        {/* ── How it works mini steps ── */}
        <div className="mx-4 mb-3 bg-[#0F1729] border border-[#2B3654] rounded-xl p-3">
          <p className="text-[#5E7398] text-[9px] uppercase tracking-widest font-semibold mb-2">Nasıl Çalışır?</p>
          <div className="flex items-center gap-1">
            {[
              { icon: "ri-cursor-line", label: "Hover Yap" },
              { icon: "ri-pencil-fill", label: "Düzenle" },
              { icon: "ri-save-line", label: "Kaydet" },
              { icon: "ri-check-double-line", label: "Yayınla" },
            ].map((step, i) => (
              <div key={i} className="flex items-center gap-1 flex-1">
                <div className="flex flex-col items-center gap-1 flex-1">
                  <div className="w-6 h-6 flex items-center justify-center rounded-lg bg-[#141E35] border border-[#2B3654]">
                    <i className={`${step.icon} text-[#7B8FAF] text-xs`}></i>
                  </div>
                  <p className="text-[#465886] text-[9px] text-center whitespace-nowrap">{step.label}</p>
                </div>
                {i < 3 && (
                  <div className="flex-shrink-0 mb-3">
                    <i className="ri-arrow-right-line text-[#2B3654] text-xs"></i>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* ── CTA ── */}
        <div className="mx-4 mb-4 bg-amber-500/10 border border-amber-500/25 rounded-xl px-3 py-2.5 flex items-center gap-2.5">
          <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-amber-500/20 flex-shrink-0">
            <i className="ri-lock-fill text-amber-400 text-sm"></i>
          </div>
          <p className="text-amber-300/90 text-[11px] leading-relaxed flex-1">{data.ctaText}</p>
        </div>
      </div>
    </div>
  );
}
